/**
 * URL-friendly slug pour identifier une collection catalogue dans
 * l'URL côté benef. On préfère le slug au prefix_id (CCO_xxx) dans
 * la barre d'adresse pour lisibilité — la résolution slug → id se
 * fait côté front en croisant avec la liste des collections.
 *
 * Approche : lowercase + retrait des accents (NFD + strip combining
 * marks) + remplacement de tout non-alphanumérique par un tiret +
 * trim. "Mobil-homes Île de Ré" → "mobil-homes-ile-de-re".
 */
export const generateCollectionSlug = (name: string): string =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * Retrouve l'id (prefix_id ou numérique) d'une collection à partir
 * d'un slug d'URL. Tolère aussi un id brut passé en query param
 * (fallback rétro-compat pour les anciens liens / emails) — si la
 * valeur match exactement un id, on la retourne sans chercher de slug.
 * Retourne `undefined` si rien ne match (le caller ignorera le filtre).
 */
export const resolveCollectionIdFromSlug = (
  slugOrId: string | null | undefined,
  collections: ReadonlyArray<{ id: string, name: string }>,
): string | undefined => {
  if (!slugOrId) return undefined;
  const exactIdMatch = collections.find((c) => c.id === slugOrId);
  if (exactIdMatch) return exactIdMatch.id;
  const slugMatch = collections.find((c) => generateCollectionSlug(c.name) === slugOrId);
  return slugMatch?.id;
};
